import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Grid, 
  IconButton, 
  Tooltip, 
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Search } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import * as MdiIcons from '@mdi/js';
import Icon from '@mdi/react';

const PRESET_COLORS = [
  '#00d4ff', '#00ff88', '#ff4081', '#ffaa00', '#ff4444', 
  '#ae81ff', '#ffffff', '#26c6da', '#66bb6a', '#ffee58'
];

// Custom PNG icons from frontend/public directory
const CUSTOM_PNG_ICONS = [
  '2fauth-light',
  'dashboard-icons',
  'ddns-updater',
  'disk',
  'docker-moby',
  'dockge',
  'fastapi',
  'filebrowser',
  'glances-light',
  'gramps-web',
  'hacker',
  'hbo-light',
  'esphome-alt',
  'hdd',
  'home-assistant-alt',
  'immich',
  'jellyfin',
  'jellystat',
  'netflix',
  'nextcloud',
  'nvme',
  'parquet',
  'raspberry-pi',
  'reactjs',
  'sqlitebrowser',
  'ssd',
  'task_scheduler',
  'truenas-scale',
  'truenas_logo',
  'victron-energy',
  'unifi-controller',
  'unifi',
  'webdav',
  'wordpress',
  'zigbee',
  'xiaomi-global',
  'wled'
];

const IconPicker = ({ open, onClose, onSelect, fullScreen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [library, setLibrary] = useState('lucide'); // 'lucide', 'mdi', or 'custom'

  // Filter out non-icon exports and map to names for Lucide
  const lucideIcons = Object.keys(LucideIcons).filter(key => 
    typeof LucideIcons[key] === 'function' || (typeof LucideIcons[key] === 'object' && LucideIcons[key].render)
  );

  // Filter MDI icons
  const mdiIcons = Object.keys(MdiIcons).filter(key => 
    key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLucide = lucideIcons.filter(name => 
    name.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const filteredMdi = mdiIcons.slice(0, 100);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth 
      fullScreen={fullScreen || isMobile}
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          backgroundImage: 'none',
          border: (fullScreen || isMobile) ? 'none' : '1px solid rgba(255,255,255,0.1)',
          borderRadius: (fullScreen || isMobile) ? 0 : '16px',
          boxShadow: '0 24px 48px rgba(0,0,0,0.5)'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#1a1a1a', 
        color: '#fff', 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 2 : 0,
        pt: isMobile ? 3 : 2
      }}>
        <Typography variant="h6">Select Icon</Typography>
        <Tabs 
          value={library} 
          onChange={(e, v) => setLibrary(v)}
          sx={{ 
            minHeight: 0,
            width: isMobile ? '100%' : 'auto',
            '& .MuiTab-root': { 
              py: 0.5, 
              minHeight: isMobile ? 40 : 0, 
              color: 'rgba(255,255,255,0.5)',
              flex: isMobile ? 1 : 'none'
            } 
          }}
        >
          <Tab label="Lucide" value="lucide" />
          <Tab label="MDI" value="mdi" />
          <Tab label="Custom" value="custom" />
        </Tabs>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: '#1a1a1a', color: '#fff', px: isMobile ? 2 : 3 }}>
        <TextField
          fullWidth
          size={isMobile ? 'medium' : 'small'}
          placeholder="Search icons..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ 
            mb: 2, 
            mt: 1,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&:hover fieldset': { borderColor: '#00d4ff' },
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={isMobile ? 20 : 18} color="rgba(255,255,255,0.5)" />
              </InputAdornment>
            ),
          }}
        />
        <Grid container spacing={isMobile ? 2 : 1}>
          {library === 'lucide' ? (
            filteredLucide.map((iconName) => {
              const LucideIcon = LucideIcons[iconName];
              return (
                <Grid item xs={isMobile ? 3 : 2} key={iconName}>
                  <Tooltip title={iconName} disableHoverListener={isMobile}>
                    <IconButton 
                      onClick={() => onSelect({ type: 'lucide', name: iconName })}
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)',
                        width: '100%',
                        aspectRatio: '1/1',
                        '&:hover': { color: '#00d4ff', bgcolor: 'rgba(0, 212, 255, 0.1)' }
                      }}
                    >
                      <LucideIcon size={isMobile ? 28 : 24} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              );
            })
          ) : library === 'mdi' ? (
            filteredMdi.map((iconName) => (
              <Grid item xs={isMobile ? 3 : 2} key={iconName}>
                <Tooltip title={iconName} disableHoverListener={isMobile}>
                  <IconButton 
                    onClick={() => onSelect({ type: 'mdi', name: iconName })}
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)',
                      width: '100%',
                      aspectRatio: '1/1',
                      '&:hover': { color: '#00ff88', bgcolor: 'rgba(0, 255, 136, 0.1)' }
                    }}
                  >
                    <Icon path={MdiIcons[iconName]} size={isMobile ? 1.2 : 1} />
                  </IconButton>
                </Tooltip>
              </Grid>
            ))
          ) : (
            // Custom PNG icons
            CUSTOM_PNG_ICONS.filter(iconName => 
              iconName.toLowerCase().includes(searchTerm.toLowerCase())
            ).map((iconName) => (
              <Grid item xs={isMobile ? 3 : 2} key={iconName}>
                <Tooltip title={iconName} disableHoverListener={isMobile}>
                  <IconButton 
                    onClick={() => onSelect({ type: 'custom', name: iconName })}
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)',
                      width: '100%',
                      aspectRatio: '1/1',
                      '&:hover': { color: '#ffaa00', bgcolor: 'rgba(255, 170, 0, 0.1)' }
                    }}
                  >
                    <img 
                      src={`/${iconName}.png`} 
                      alt={iconName}
                      style={{ 
                        width: isMobile ? '28px' : '24px', 
                        height: isMobile ? '28px' : '24px',
                        objectFit: 'contain'
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Grid>
            ))
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default IconPicker;
export { LucideIcons, MdiIcons, PRESET_COLORS };
